import React, { lazy, Suspense, useState, useCallback, useEffect } from "react";
import { Bot } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { OPEN_CHAT_EVENT } from "@/lib/chatEvents";

// Heavy ChatWidget (Drawer + Radix + AI session) is lazy-loaded on the first
// user interaction so it doesn't block the LCP.
const ChatWidget = lazy(() => import("@/components/ChatWidget"));

/**
 * LazyChatLauncher
 * ----------------
 * Renders the floating chat button (desktop/tablet only — on mobile the chat
 * entry point lives inside the bottom StickyCTA bar so it never covers the
 * content). Lazy-loads the heavy ChatWidget on the first user interaction
 * (hover/focus/click or the global OPEN_CHAT_EVENT) and then drives `open`
 * state externally.
 *
 * Why is the launcher button ALWAYS mounted (never replaced by the widget's
 * internal trigger)? Because Vaul/Radix Drawers historically render their
 * trigger as a sibling. On mobile, touchstart used to flip `loaded=true`
 * which unmounted the old button BEFORE the click event reached its target,
 * making the drawer fail to open. Keeping the launcher button stable in the
 * DOM and driving the drawer via a controlled `open` prop fixes this race
 * condition entirely.
 */
export default function LazyChatLauncher() {
  const { t } = useLang();
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  // Preload (hover/focus) does not open the drawer; click does both.
  // Note: we intentionally do NOT preload on touchstart. On mobile, touchstart
  // fires a render BEFORE the synthetic click event, which previously caused
  // the click to be lost (DOM target swapped under the user's finger).
  const preload = useCallback(() => setLoaded(true), []);

  const handleClick = useCallback(() => {
    setLoaded(true);
    setOpen(true);
  }, []);

  // Global event: lets the mobile sticky bar (or any other component) open
  // the chat without prop-drilling.
  useEffect(() => {
    const onOpen = () => {
      setLoaded(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_CHAT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, onOpen);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={preload}
        onFocus={preload}
        className="hidden md:inline-flex fixed right-4 bottom-6 z-40 items-center gap-2 rounded-full bg-[hsl(var(--brand-aqua))] text-white pl-3 pr-4 h-12 shadow-lg press hover:bg-[hsl(var(--brand-aqua-2))]"
        data-testid="ai-chat-open-button"
        aria-label="Open AI assistant"
      >
        <Bot className="h-5 w-5" />
        <span className="text-sm font-semibold">{t("chat.title")}</span>
      </button>
      {loaded ? (
        <Suspense fallback={null}>
          <ChatWidget open={open} onOpenChange={setOpen} />
        </Suspense>
      ) : null}
    </>
  );
}
